import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { ApiError, setUnauthorizedHandler } from "./api";
import { fetchAuthUser, type AuthUser } from "./auth";
import { showError } from "./toast";

// 認証状態。
//   - loading  : 起動時のセッション復元プローブ中
//   - authed   : 認証済み（user が入る）
//   - unauthed : 未ログイン（401 が返った＝セッション無効）。ログインへ誘導する。
//   - error    : バックエンド到達不能（通信断・5xx・タイムアウト）。Cookie は有効な
//                可能性が高いためログインへは飛ばさず、再試行を促す。dev の夜間
//                自動停止やコールドスタート中に 401 と誤認しないための区別。
export type AuthStatus = "loading" | "authed" | "unauthed" | "error";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  // 明示的に再取得する（ログイン直後・再試行など）。
  refresh: () => Promise<void>;
  // ログアウト後などにローカル状態だけ未認証へ落とす。
  clear: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// アプリ全体で一度だけ `GET /auth` を実行し、ヘッダ・認証ガードで共有する。
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    setStatus("loading");
    // 401 は「未ログイン」で確定だが、通信断・5xx・タイムアウトは一時的障害であり
    // Cookie は有効な可能性が高い。両者を区別し、後者は数回リトライしてから error に倒す
    // （dev のコールドスタート＝起動直後の数秒で unauthed に誤判定しないため）。
    for (let attempt = 0; ; attempt++) {
      try {
        const u = await fetchAuthUser();
        setUser(u);
        setStatus("authed");
        return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setUser(null);
          setStatus("unauthed");
          return;
        }
        if (attempt >= 2) {
          // 予期しないエラーはログに残す（画面上は再試行を促す）。
          console.error("認証状態の取得に失敗しました", e);
          setUser(null);
          setStatus("error");
          return;
        }
        // 線形バックオフ（1s, 2s）で再試行する。
        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }, []);

  const clear = useCallback(() => {
    setUser(null);
    setStatus("unauthed");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // 操作中に 401 を受けたら、画面ごとにエラーを出さず一元的にログインへ誘導する。
  // AuthGate が unauthed を見て /login へ飛ばす。
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus((prev) => {
        // 既に未認証なら通知を重ねない（連続した API 呼び出しで複数回 401 に
        // なることがあるため）。
        if (prev !== "unauthed") {
          showError("セッションが切れました。再度ログインしてください。");
        }
        return "unauthed";
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, refresh, clear }}>
      {children}
    </AuthContext.Provider>
  );
}

// AuthProvider と対で使うため同居させる（Fast Refresh の警告は意図的に抑止）。
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth は AuthProvider の内側で使用してください");
  }
  return ctx;
}
